/**
 * Domain utility functions for extracting company domains from email addresses
 */

/**
 * Common email domain mappings to their company website domains
 * Maps marketing/transactional email domains to the actual company domain
 */
const EMAIL_DOMAIN_MAPPINGS: Record<string, string> = {
  // Airlines
  'ryanairemail.com': 'ryanair.com',
  'ryanairmail.com': 'ryanair.com',
  'email.united.com': 'united.com',
  'e.united.com': 'united.com',
  'aa.com': 'aa.com',
  'email.aa.com': 'aa.com',
  'news.southwest.com': 'southwest.com',
  'email.delta.com': 'delta.com',
  'email.jetblue.com': 'jetblue.com',

  // Social Media
  'redditmail.com': 'reddit.com',
  'facebookmail.com': 'facebook.com',
  'twittermail.com': 'twitter.com',
  'mail.instagram.com': 'instagram.com',
  'em.linkedin.com': 'linkedin.com',
  'linkedin.com': 'linkedin.com',
  'tiktok.com': 'tiktok.com',
  'mail.tiktok.com': 'tiktok.com',

  // E-commerce
  'amazon.com': 'amazon.com',
  'email.amazon.com': 'amazon.com',
  'gc.email.amazon.com': 'amazon.com',
  'shipmenttracking.amazon.com': 'amazon.com',
  'a]mazon.com': 'amazon.com',
  'ebay.com': 'ebay.com',
  'reply.ebay.com': 'ebay.com',
  'ebaymail.com': 'ebay.com',
  'etsy.com': 'etsy.com',
  'mail.etsy.com': 'etsy.com',
  'shopify.com': 'shopify.com',
  'email.shopify.com': 'shopify.com',

  // Tech Companies
  'google.com': 'google.com',
  'accounts.google.com': 'google.com',
  'apple.com': 'apple.com',
  'id.apple.com': 'apple.com',
  'email.apple.com': 'apple.com',
  'microsoft.com': 'microsoft.com',
  'mail.microsoft.com': 'microsoft.com',
  'accountprotection.microsoft.com': 'microsoft.com',
  'netflix.com': 'netflix.com',
  'mailer.netflix.com': 'netflix.com',
  'spotifymail.com': 'spotify.com',
  'spotify.com': 'spotify.com',

  // Financial
  'paypal.com': 'paypal.com',
  'mail.paypal.com': 'paypal.com',
  'venmo.com': 'venmo.com',
  'mail.venmo.com': 'venmo.com',
  'email.chase.com': 'chase.com',
  'chase.com': 'chase.com',
  'americanexpress.com': 'americanexpress.com',
  'email.americanexpress.com': 'americanexpress.com',

  // Food & Delivery
  'uber.com': 'uber.com',
  'ubereats.com': 'ubereats.com',
  'doordash.com': 'doordash.com',
  'grubhub.com': 'grubhub.com',
  'postmates.com': 'postmates.com',
  'instacart.com': 'instacart.com',

  // Retail
  'e.target.com': 'target.com',
  'target.com': 'target.com',
  'email.walmart.com': 'walmart.com',
  'walmart.com': 'walmart.com',
  'email.costco.com': 'costco.com',
  'costco.com': 'costco.com',
  'em.kohls.com': 'kohls.com',
  'kohls.com': 'kohls.com',
  'email.bestbuy.com': 'bestbuy.com',
  'bestbuy.com': 'bestbuy.com',

  // Travel
  'booking.com': 'booking.com',
  'mail.booking.com': 'booking.com',
  'airbnb.com': 'airbnb.com',
  'email.airbnb.com': 'airbnb.com',
  'expedia.com': 'expedia.com',
  'mail.expedia.com': 'expedia.com',
  'hotels.com': 'hotels.com',
  'mail.hotels.com': 'hotels.com',
}

/**
 * Common email subdomain prefixes to strip
 */
const EMAIL_PREFIXES_TO_STRIP = [
  'email.',
  'mail.',
  'e.',
  'em.',
  'news.',
  'newsletter.',
  'noreply.',
  'no-reply.',
  'info.',
  'support.',
  'notifications.',
  'mailer.',
  'updates.',
  'promo.',
  'marketing.',
  'reply.',
  'bounce.',
  'transactional.',
  'accounts.',
  'account.',
  'notify.',
  'alerts.',
  'gc.email.',
]

/**
 * Common email domain suffixes that indicate marketing/transactional emails
 */
const EMAIL_SUFFIXES_TO_REPLACE = [
  'mail.com',  // e.g., ryanairemail.com -> ryanair.com
  'email.com',
  'emails.com',
]

/**
 * Extract company domain from an email address
 * Maps marketing email domains to their actual company websites
 */
export function extractCompanyDomain(emailAddress: string): string | null {
  if (!emailAddress) return null

  // Extract domain from email address (handles formats like "Name <email@domain.com>")
  const match = emailAddress.match(/@([^>\s]+)/)
  if (!match) return null

  let domain = match[1].toLowerCase().trim()

  // Check exact mapping first
  if (EMAIL_DOMAIN_MAPPINGS[domain]) {
    return EMAIL_DOMAIN_MAPPINGS[domain]
  }

  // Try stripping common prefixes
  for (const prefix of EMAIL_PREFIXES_TO_STRIP) {
    if (domain.startsWith(prefix)) {
      const strippedDomain = domain.substring(prefix.length)
      // Check if stripped version has a mapping
      if (EMAIL_DOMAIN_MAPPINGS[strippedDomain]) {
        return EMAIL_DOMAIN_MAPPINGS[strippedDomain]
      }
      domain = strippedDomain
      break
    }
  }

  // Try replacing mail suffix patterns (e.g., ryanairemail.com -> ryanair.com)
  for (const suffix of EMAIL_SUFFIXES_TO_REPLACE) {
    if (domain.endsWith(suffix)) {
      const baseName = domain.slice(0, -suffix.length)
      if (baseName.length > 2) {
        return `${baseName}.com`
      }
    }
  }

  // Handle subdomain patterns: remove common subdomains
  const parts = domain.split('.')
  if (parts.length > 2) {
    // Check if first part is a common email subdomain
    const commonSubdomains = ['email', 'mail', 'e', 'em', 'news', 'newsletter', 'noreply', 'info', 'support', 'notifications', 'mailer']
    if (commonSubdomains.includes(parts[0])) {
      domain = parts.slice(1).join('.')
    }
  }

  return domain
}

/**
 * Get favicon URL for a domain using Google's favicon service
 */
export function getFaviconUrl(domain: string, size: number = 64): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`
}

/**
 * Get the best favicon URL for an email address
 */
export function getFaviconUrlForEmail(emailAddress: string, size: number = 64): string | null {
  const domain = extractCompanyDomain(emailAddress)
  if (!domain) return null
  return getFaviconUrl(domain, size)
}
